import React from "react";
import AsyncSelect from "react-select/async";
import { MultiValue, ActionMeta, StylesConfig, components } from "react-select";

export interface SelectOption {
  value: string;
  label: string;
}

interface MultiSelectAsyncProps {
  value: MultiValue<SelectOption>;
  onChange: (value: MultiValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => void;
  isSearchable?: boolean;
  loadOptions: (inputValue: string) => Promise<SelectOption[]>; 
  defaultOptions?: boolean | SelectOption[];
  closeMenuOnSelect?: boolean;
  options: SelectOption[];
}

const ValueContainer = ({ children, ...props }: any) => {
  const selected = props.getValue();
  const inputValue = props.selectProps.inputValue;
  const displayLabel = `${selected.length} item${selected.length !== 1 ? 's' : ''} selected`;
  const shouldShowOverlay = selected.length > 0 && (inputValue === "" || inputValue === undefined);

  return (
    <components.ValueContainer {...props}>
      {children}
      {shouldShowOverlay && (
        <div style={{ 
          position: "absolute", 
          left: "10px", 
          top: "50%", 
          transform: "translateY(-50%)", 
          pointerEvents: "none", 
          color: "#444", 
          fontWeight: "normal",
          textTransform: "none",
          fontFamily: "inherit",
          fontSize: "14px"
          }}>
          {displayLabel}
        </div>
      )}
    </components.ValueContainer>
  );
};

export const MultiSelectAsync: React.FC<MultiSelectAsyncProps> = ({
  value,
  onChange,
  isSearchable = true,
  loadOptions,
  defaultOptions = true,
  closeMenuOnSelect = true,
  options,
}) => {
  const handleChange = (newValue: MultiValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => {
    onChange(newValue, actionMeta);
  };

  const handleSelectAll = (event: React.MouseEvent) => {
    event.preventDefault();
    onChange(options, { action: 'select-option', option: undefined });
  };
  
  const handleDeselectAll = (event: React.MouseEvent) => {
    event.preventDefault();
    onChange([], { action: 'deselect-option', option: undefined });
  };

  const MenuList = (props: any) => {
    const buttonStyle: React.CSSProperties = {
      width: 'calc(50% - 4px)',
      padding: '4px 0',
      margin: '0 2px',
      cursor: 'pointer',
      backgroundColor: 'white',
      border: '1px solid #ccc',
      borderRadius: '3px',
      textAlign: 'center',
      transition: 'background-color 0.2s',
    };
    const buttonHoverStyle: React.CSSProperties = { backgroundColor: '#f0f0f0' };

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px' }}>
          <div
            style={buttonStyle}
            role="button"
            onMouseDown={handleSelectAll} // Added instead of onClick
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = buttonHoverStyle.backgroundColor!)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor!)}
          >
            Select All
          </div>
          <div
            style={buttonStyle}
            role="button"
            onMouseDown={handleDeselectAll} // Added instead of onClick
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = buttonHoverStyle.backgroundColor!)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor!)}
          >
            Deselect All
          </div>
        </div>
        <components.MenuList {...props}>{props.children}</components.MenuList>
      </div>
    );
  };

  const customStyles: StylesConfig<SelectOption, true> = {
    multiValue: (base) => ({ ...base, display: 'none' }),
    valueContainer: (base) => ({ 
      ...base,
      textTransform: "none",
      fontWeight: "normal",
      fontFamily: "inherit", 
    }),
    placeholder: (base) => ({
      ...base,
      fontWeight: "normal",
    }),
    control: (base) => ({
      ...base,
      fontWeight: "normal",
    })
  };

  return (
    <AsyncSelect<SelectOption, true>
      value={value}
      onChange={handleChange}
      instanceId="multi-select-async-unique-id"
      aria-label={`Select Items`}
      isMulti
      isSearchable={isSearchable}
      loadOptions={loadOptions}
      defaultOptions={defaultOptions}
      hideSelectedOptions={false} // Added to always show options in dropdown
      styles={customStyles}
      components={{ ValueContainer, MenuList }}
      placeholder="Select..."
      closeMenuOnSelect={false}
      tabSelectsValue={false} 
      menuPortalTarget={document.body} // Added to fix form closing issue
    />
  );
};
